import { useState, useEffect, useRef } from "react";

// ── HOOK MOBILE ──────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ── DATOS INICIALES ──────────────────────────────────────────────
const VENDEDORES = [
  { id: "admin", nombre: "Admin", pin: "1234", rol: "admin" },
];

const MERCADOS_INICIALES = ["LATAM", "China", "France", "Italy", "Germany"];
const EMPRESAS_CAPTIO = ["All", "FL"];

// Paleta FL — navy + gold
const C = {
  navy:      "#1A2B4A",
  navyMid:   "#2D4470",
  navyLite:  "#6B7A94",
  gold:      "#C8972A",
  goldLight: "#E8B84B",
  white:     "#FFFFFF",
  bg:        "#F5F4F0",
  bgWarm:    "#ECEAE4",
  rule:      "rgba(26,43,74,0.12)",
};

const COLUMNAS = [
  { id: "New",      label: "New",      color: "#6B7A94", bg: "rgba(107,122,148,0.10)" },
  { id: "Validated",label: "Validated",color: "#2D4470", bg: "rgba(45,68,112,0.10)"  },
  { id: "Quoted",   label: "Quoted",   color: "#C8972A", bg: "rgba(200,151,42,0.10)" },
  { id: "Won",      label: "Won",      color: "#1A2B4A", bg: "rgba(26,43,74,0.10)"   },
  { id: "Lost",     label: "Lost",     color: "#EF4444", bg: "rgba(239,68,68,0.10)"  },
];

const FUENTE_ICON = {
  "SerpAPI": "🔍", "LinkedIn": "💼", "Manual": "📁",
  "Referral": "🤝", "Web": "🌐", "Email": "📧",
};

const LEADS_DEMO = [
  { id:1, empresa:"TechStar Shanghai Ltd", contacto:"Li Wei", cargo:"IP Manager", email:"lwei@techstar.cn", tel:"+86 21 5555 0001", ciudad:"Shanghai", region:"China", segmento:"Technology", fuente:"SerpAPI", estado:"New", vendedor:"admin", score:91, monto:0, fecha:"2026-06-01", notas:"Expanding to LATAM market", empresa_captio:"FL", mercado:"China" },
  { id:2, empresa:"Maison Beauté Paris SAS", contacto:"Claire Dupont", cargo:"Legal Director", email:"cdupont@maisonbeaute.fr", tel:"+33 1 4455 6677", ciudad:"Paris", region:"France", segmento:"Fashion & Luxury", fuente:"LinkedIn", estado:"Validated", vendedor:"admin", score:87, monto:0, fecha:"2026-06-03", notas:"Needs trademark registration in Argentina and Brazil", empresa_captio:"FL", mercado:"France" },
  { id:3, empresa:"Grupo Innovación SA", contacto:"Carlos Méndez", cargo:"CEO", email:"cmendez@grupoinnovacion.com", tel:"+54 11 4567 8901", ciudad:"Buenos Aires", region:"LATAM", segmento:"Technology", fuente:"SerpAPI", estado:"Quoted", vendedor:"admin", score:94, monto:2800, fecha:"2026-06-05", notas:"Quote sent for LATAM trademark package", empresa_captio:"FL", mercado:"LATAM" },
  { id:4, empresa:"Automazione Italia SpA", contacto:"Marco Rossi", cargo:"Head of Legal", email:"mrossi@automazione.it", tel:"+39 02 1234 5678", ciudad:"Milano", region:"Italy", segmento:"Industrial", fuente:"LinkedIn", estado:"Won", vendedor:"admin", score:89, monto:3500, fecha:"2026-05-28", notas:"Argentina + Brazil trademark registration confirmed", empresa_captio:"FL", mercado:"Italy" },
  { id:5, empresa:"Chemie AG Hamburg", contacto:"Hans Müller", cargo:"IP Counsel", email:"hmuller@chemieag.de", tel:"+49 40 9876 5432", ciudad:"Hamburg", region:"Germany", segmento:"Chemical & Pharma", fuente:"Manual", estado:"New", vendedor:"admin", score:76, monto:0, fecha:"2026-06-08", notas:"Interested in patent filing in Argentina", empresa_captio:"FL", mercado:"Germany" },
];

// ── HELPERS ──────────────────────────────────────────────────────
const fmt$ = v => v ? `USD ${Number(v).toLocaleString("en-US")}` : "—";
const scoreColor = s => s >= 85 ? "#10B981" : s >= 68 ? "#C8972A" : "#EF4444";

function useLocalStorage(key, initial) {
  const [val, setVal] = useState(() => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : initial; }
    catch { return initial; }
  });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} }, [key, val]);
  return [val, setVal];
}

function useLeadsFromFile(setLeads) {
  useEffect(() => {
    fetch("https://fl.velqo.io/api/leads")
      .then(r => { if (!r.ok) throw new Error("No server"); return r.json(); })
      .then(data => {
        if (!Array.isArray(data) || data.length === 0) return;
        const normalized = data.map((l, i) => ({
          id:       l.id || i + 1,
          empresa:  l.empresa || "",
          contacto: l.contacto || l.nombre_contacto || "",
          cargo:    l.cargo_decisor || l.cargo || "",
          email:    l.email_estimado || l.email || "",
          tel:      l.telefono || l.tel || "",
          ciudad:   l.ciudad || "",
          region:   l.region || l.mercado || "LATAM",
          mercado:  l.mercado || l.region || "LATAM",
          segmento: l.segmento || "",
          fuente:   l.fuente || "Agent AI",
          estado:   l.estado || "New",
          vendedor: l.vendedor || "admin",
          score:    Number(l.score) || 70,
          monto:    Number(l.monto) || 0,
          fecha:    l.fecha || new Date().toISOString().split("T")[0],
          notas:          l.notas || "",
          empresa_captio: l.empresa_captio || "FL",
        }));
        localStorage.removeItem("fl_leads");
        setLeads(normalized);
      })
      .catch(err => console.warn("Server unavailable, using local data:", err));
  }, []);
}

// ── COMPONENTES BASE ─────────────────────────────────────────────
function ScoreBar({ score }) {
  const c = scoreColor(score);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
      <div style={{ flex:1, height:3, background:"rgba(26,43,74,0.08)", borderRadius:2, overflow:"hidden" }}>
        <div style={{ width:`${score}%`, height:"100%", background:c, borderRadius:2 }}/>
      </div>
      <span style={{ fontSize:10, color:c, fontWeight:700, minWidth:22 }}>{score}</span>
    </div>
  );
}

// ── MODAL MOVER LEAD ─────────────────────────────────────────────
function ModalMover({ lead, destino, onConfirm, onCancel }) {
  const [monto, setMonto] = useState(lead.monto || "");
  const [motivo, setMotivo] = useState("");
  const necesitaMonto  = destino === "Quoted" || destino === "Won";
  const necesitaMotivo = destino === "Lost";
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16 }}>
      <div style={{ background:C.bg, border:`1px solid ${C.rule}`, borderRadius:16, padding:28, width:"100%", maxWidth:400 }}>
        <div style={{ fontSize:16, fontWeight:700, color:C.navy, marginBottom:6 }}>
          Move to <span style={{ color: COLUMNAS.find(c=>c.id===destino)?.color }}>{destino}</span>
        </div>
        <div style={{ fontSize:13, color:"rgba(26,43,74,0.5)", marginBottom:20 }}>{lead.empresa}</div>
        {necesitaMonto && (
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:11, color:"rgba(26,43,74,0.4)", textTransform:"uppercase", letterSpacing:1 }}>
              {destino === "Quoted" ? "Quote amount (USD)" : "Closing amount (USD)"}
            </label>
            <input type="number" value={monto} onChange={e => setMonto(e.target.value)} placeholder="e.g. 2500"
              style={{ width:"100%", marginTop:6, background:"#fff", border:`1px solid ${C.rule}`, borderRadius:8, padding:"10px 14px", color:C.navy, fontSize:14, outline:"none", boxSizing:"border-box" }}
            />
          </div>
        )}
        {necesitaMotivo && (
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:11, color:"rgba(26,43,74,0.4)", textTransform:"uppercase", letterSpacing:1 }}>Reason for loss</label>
            <select value={motivo} onChange={e => setMotivo(e.target.value)}
              style={{ width:"100%", marginTop:6, background:"#fff", border:`1px solid ${C.rule}`, borderRadius:8, padding:"10px 14px", color:C.navy, fontSize:13, outline:"none", boxSizing:"border-box" }}>
              <option value="">Select reason...</option>
              <option value="Price">Price — chose a cheaper provider</option>
              <option value="No response">No response — didn't reply</option>
              <option value="Not qualified">Not qualified — wrong profile</option>
              <option value="Competition">Competition — chose a competitor</option>
              <option value="Postponed">Postponed — not the right time</option>
              <option value="Other">Other reason</option>
            </select>
          </div>
        )}
        <div style={{ display:"flex", gap:10, marginTop:8 }}>
          <button onClick={onCancel} style={{ flex:1, padding:"10px 0", borderRadius:8, border:`1px solid ${C.rule}`, background:"transparent", color:"rgba(26,43,74,0.5)", cursor:"pointer", fontSize:13 }}>Cancel</button>
          <button onClick={() => onConfirm({ monto: Number(monto) || 0, motivo })} disabled={necesitaMotivo && !motivo}
            style={{ flex:2, padding:"10px 0", borderRadius:8, border:"none", background: COLUMNAS.find(c=>c.id===destino)?.color || C.navy, color:"#fff", cursor:"pointer", fontSize:13, fontWeight:700, opacity: (necesitaMotivo && !motivo) ? 0.4 : 1 }}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MODAL DETALLE LEAD ───────────────────────────────────────────
function ModalDetalle({ lead, vendedores, onClose, onAsignar, esAdmin, usuarioActual }) {
  const [enviandoEmail, setEnviandoEmail] = useState(null);
  const [emailLog, setEmailLog]           = useState([]);
  const [loadingLog, setLoadingLog]       = useState(false);
  const empKey = "fl";

  useEffect(() => {
    setLoadingLog(true);
    fetch(`https://fl.velqo.io/api/actividad?lead_id=${lead.id}&limit=20`)
      .then(r => r.json())
      .then(data => { setEmailLog(Array.isArray(data) ? data : []); setLoadingLog(false); })
      .catch(() => setLoadingLog(false));
  }, [lead.id]);

  const enviarEmail = (tipo) => {
    const labels = { presentacion: "introduction email", seguimiento: "follow-up email", propuesta: "proposal email" };
    if (!window.confirm(`Send ${labels[tipo] || tipo} to ${lead.empresa}?`)) return;
    setEnviandoEmail(tipo);
    fetch(`https://fl.velqo.io/api/email/${tipo}/${empKey}/${lead.id}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enviado_por: usuarioActual?.id || "admin" }),
    })
    .then(r => r.json())
    .then(d => {
      setEnviandoEmail(null);
      if (d.ok) {
        alert("✅ Email sent successfully");
        fetch(`https://fl.velqo.io/api/actividad?lead_id=${lead.id}&limit=20`)
          .then(r => r.json()).then(data => setEmailLog(Array.isArray(data) ? data : [])).catch(() => {});
      } else { alert(`❌ Error sending: ${d.error}`); }
    })
    .catch(() => { setEnviandoEmail(null); alert("❌ Connection error"); });
  };

  const vendedor = vendedores.find(v => v.id === lead.vendedor);
  const tipoCol = { presentacion:"#3B82F6", seguimiento:C.gold, propuesta:"#10B981" };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16 }}>
      <div style={{ background:C.bg, border:`1px solid ${C.rule}`, borderRadius:16, padding:28, width:"100%", maxWidth:500, maxHeight:"90vh", overflowY:"auto" }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:20 }}>
          <div>
            <div style={{ fontSize:17, fontWeight:800, color:C.navy }}>{lead.empresa}</div>
            <div style={{ fontSize:12, color:C.navyLite, marginTop:2 }}>{lead.segmento} · {lead.mercado || lead.region}</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:C.navyLite, cursor:"pointer", fontSize:20 }}>✕</button>
        </div>

        {[
          ["Contact", lead.contacto],
          ["Role", lead.cargo],
          ["Email", lead.email],
          ["Phone", lead.tel],
          ["City", lead.ciudad],
          ["Market", lead.mercado || lead.region],
          ["Segment", lead.segmento],
          ["Source", lead.fuente],
          ["Score", lead.score],
          ["Amount", lead.monto ? fmt$(lead.monto) : "—"],
          ["Date", lead.fecha],
        ].map(([k, v]) => v ? (
          <div key={k} style={{ display:"flex", gap:12, padding:"7px 0", borderBottom:`1px solid rgba(26,43,74,0.06)` }}>
            <span style={{ fontSize:12, color:C.navyLite, minWidth:80 }}>{k}</span>
            <span style={{ fontSize:13, color:C.navy, fontWeight:500, flex:1 }}>{String(v)}</span>
          </div>
        ) : null)}

        {lead.notas && (
          <div style={{ marginTop:12, padding:12, background:"rgba(200,151,42,0.08)", border:`1px solid rgba(200,151,42,0.2)`, borderRadius:8 }}>
            <div style={{ fontSize:10, color:C.gold, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>Notes</div>
            <div style={{ fontSize:13, color:C.navy, lineHeight:1.6 }}>{lead.notas}</div>
          </div>
        )}

        {/* Emails */}
        <div style={{ marginTop:20 }}>
          <div style={{ fontSize:13, fontWeight:700, color:C.navy, marginBottom:12 }}>📧 Send email</div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {[
              { tipo:"presentacion", label:"Introduction email", color:"#3B82F6" },
              { tipo:"seguimiento",  label:"Follow-up email",    color:C.gold    },
              { tipo:"propuesta",    label:"Proposal email",     color:"#10B981" },
            ].map(({ tipo, label, color }) => (
              <button key={tipo} onClick={() => enviarEmail(tipo)} disabled={!!enviandoEmail}
                style={{ padding:"10px 16px", borderRadius:8, border:`1px solid ${color}40`, background:`${color}12`, color, fontWeight:600, fontSize:13, cursor: enviandoEmail ? "not-allowed" : "pointer", textAlign:"left", opacity: enviandoEmail ? 0.6 : 1 }}>
                {enviandoEmail === tipo ? "⏳ Sending..." : `📤 ${label}`}
              </button>
            ))}
          </div>
        </div>

        {/* Historial emails */}
        {emailLog.length > 0 && (
          <div style={{ marginTop:20 }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.navy, marginBottom:8 }}>Email history</div>
            {emailLog.slice(0,5).map((log, i) => (
              <div key={i} style={{ display:"flex", gap:8, alignItems:"center", padding:"6px 0", borderBottom:`1px solid rgba(26,43,74,0.05)` }}>
                <span style={{ fontSize:13 }}>{log.estado === "enviado" ? "✅" : "❌"}</span>
                <span style={{ fontSize:11, color: tipoCol[log.tipo] || C.navyLite, background:`${tipoCol[log.tipo] || C.navyLite}15`, padding:"2px 8px", borderRadius:10 }}>{log.tipo}</span>
                <span style={{ fontSize:11, color:C.navyLite, flex:1 }}>{new Date(log.creado_en).toLocaleString("en-US", { day:"2-digit", month:"2-digit", year:"2-digit", hour:"2-digit", minute:"2-digit" })}</span>
              </div>
            ))}
          </div>
        )}

        {esAdmin && (
          <div style={{ marginTop:16 }}>
            <div style={{ fontSize:11, color:C.navyLite, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Assign to</div>
            <select value={lead.vendedor || ""} onChange={e => onAsignar(lead.id, e.target.value)}
              style={{ width:"100%", background:"#fff", border:`1px solid ${C.rule}`, borderRadius:8, padding:"9px 12px", color:C.navy, fontSize:13, outline:"none" }}>
              <option value="">Unassigned</option>
              {vendedores.map(v => <option key={v.id} value={v.id}>{v.nombre}</option>)}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}

// ── CARD LEAD ────────────────────────────────────────────────────
function LeadCard({ lead, columnaColor, onDragStart, onTap }) {
  const mercadoColors = {
    "LATAM":   { bg:"rgba(16,185,129,0.12)",  color:"#0D9268" },
    "China":   { bg:"rgba(239,68,68,0.12)",   color:"#DC2626" },
    "France":  { bg:"rgba(59,130,246,0.12)",  color:"#2563EB" },
    "Italy":   { bg:"rgba(34,197,94,0.12)",   color:"#16A34A" },
    "Germany": { bg:"rgba(107,114,128,0.12)", color:"#4B5563" },
  };
  const mc = mercadoColors[lead.mercado || lead.region] || { bg:"rgba(200,151,42,0.12)", color:C.gold };
  return (
    <div draggable onDragStart={e => onDragStart(e, lead.id)} onClick={() => onTap(lead)}
      style={{ background:"#fff", border:`1px solid rgba(26,43,74,0.09)`, borderLeft:`3px solid ${columnaColor}`, borderRadius:10, padding:"12px 14px", cursor:"grab", marginBottom:8, transition:"transform .15s, box-shadow .15s" }}
      onMouseEnter={e => { e.currentTarget.style.transform="translateY(-1px)"; e.currentTarget.style.boxShadow="0 4px 16px rgba(26,43,74,0.12)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow=""; }}>
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
        <span style={{ fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:10, background:mc.bg, color:mc.color, whiteSpace:"nowrap" }}>
          {lead.mercado || lead.region}
        </span>
      </div>
      <div style={{ fontSize:13, fontWeight:700, color:C.navy, marginBottom:2, lineHeight:1.3 }}>{lead.empresa}</div>
      <div style={{ fontSize:11, color:C.navyLite, marginBottom:8 }}>{lead.contacto} · {lead.cargo}</div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
        <span style={{ fontSize:10, color:C.navyLite }}>{FUENTE_ICON[lead.fuente] || "📌"} {lead.fuente}</span>
        <span style={{ fontSize:10, color:C.navyLite }}>{lead.segmento}</span>
      </div>
      <ScoreBar score={lead.score}/>
      {lead.monto > 0 && <div style={{ marginTop:6, fontSize:12, fontWeight:700, color:C.gold }}>{fmt$(lead.monto)}</div>}
    </div>
  );
}

// ── COLUMNA KANBAN ───────────────────────────────────────────────
function Columna({ col, leads, onDrop, onDragOver, onDragStart, onTap }) {
  const total$ = leads.filter(l=>l.monto>0).reduce((a,b)=>a+b.monto,0);
  return (
    <div style={{ flex:1, minWidth:0 }} onDragOver={onDragOver} onDrop={e => onDrop(e, col.id)}>
      <div style={{ background:col.bg, border:`1px solid ${col.color}33`, borderRadius:12, padding:"10px 12px", marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <span style={{ fontSize:12, fontWeight:700, color:col.color }}>{col.label}</span>
          <span style={{ fontSize:11, color:C.navyLite, marginLeft:8 }}>{leads.length}</span>
        </div>
        {total$ > 0 && <span style={{ fontSize:11, fontWeight:700, color:col.color }}>{fmt$(total$)}</span>}
      </div>
      <div style={{ minHeight:120 }}>
        {leads.map(lead => (
          <LeadCard key={lead.id} lead={lead} columnaColor={col.color} onDragStart={onDragStart} onTap={onTap}/>
        ))}
      </div>
    </div>
  );
}

// ── LOGIN ────────────────────────────────────────────────────────
function Login({ vendedores, onLogin }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);

  const intentar = () => {
    const v = vendedores.find(v => v.pin === pin);
    if (v) { setError(false); onLogin(v); }
    else { setError(true); setTimeout(() => setError(false), 2000); }
  };

  return (
    <div style={{ minHeight:"100vh", background:C.navy, display:"flex", alignItems:"center", justifyContent:"center", padding:24, fontFamily:"Georgia,'Times New Roman',serif" }}>
      <div style={{ textAlign:"center", maxWidth:360 }}>
        <div style={{ fontSize:11, letterSpacing:"0.22em", textTransform:"uppercase", color:C.gold, marginBottom:16 }}>
          Fernández Lacort
        </div>
        <div style={{ fontFamily:"Georgia,serif", fontSize:13, color:"rgba(255,255,255,0.5)", marginBottom:48, lineHeight:1.6, letterSpacing:"0.05em" }}>
          Intellectual Property · Latin America
        </div>

        <input
          type="password"
          value={pin}
          onChange={e => setPin(e.target.value)}
          onKeyDown={e => e.key === "Enter" && intentar()}
          placeholder="PIN"
          maxLength={6}
          style={{
            width:"100%", padding:"14px", textAlign:"center", fontSize:20, letterSpacing:"0.3em",
            background:"rgba(255,255,255,0.06)", border:`1px solid ${error ? "#EF4444" : "rgba(255,255,255,0.15)"}`,
            borderRadius:8, color:"#fff", outline:"none", boxSizing:"border-box", marginBottom:16,
            fontFamily:"Georgia,serif", transition:"border-color 0.2s",
          }}
        />

        <button onClick={intentar}
          style={{ width:"100%", padding:"14px", background:C.gold, color:C.navy, border:"none", fontFamily:"Georgia,serif", fontSize:13, fontWeight:700, cursor:"pointer", borderRadius:8, letterSpacing:"0.05em", transition:"opacity 0.2s" }}
          onMouseEnter={e => e.target.style.opacity = "0.85"}
          onMouseLeave={e => e.target.style.opacity = "1"}>
          {error ? "Incorrect PIN" : "Enter"}
        </button>

        <div style={{ marginTop:48, fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase", color:"rgba(255,255,255,0.2)" }}>
          by velqo.io
        </div>
      </div>
    </div>
  );
}

// ── KNOWLEDGE VIEWER ──────────────────────────────────────────────
function KnowledgeViewer({ empresa }) {
  const [data, setData] = useState({ texto:"", chars:0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`https://fl.velqo.io/api/knowledge/${empresa}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [empresa]);

  const limpiar = () => {
    if (!window.confirm("Delete all knowledge for this company?")) return;
    fetch(`https://fl.velqo.io/api/knowledge/${empresa}/clear`, { method:"POST" })
      .then(() => setData({ texto:"", chars:0 })).catch(() => {});
  };

  return (
    <div style={{ background:"#fff", border:`1px solid rgba(26,43,74,0.09)`, borderRadius:14, padding:20 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:13, color:C.navy }}>Current knowledge</div>
          <div style={{ fontSize:10, color:C.navyLite, marginTop:2 }}>
            {data.chars > 0 ? `${data.chars} characters loaded` : "No knowledge loaded yet"}
          </div>
        </div>
        {data.chars > 0 && (
          <button onClick={limpiar} style={{ padding:"5px 12px", borderRadius:7, border:"1px solid rgba(239,68,68,0.3)", background:"rgba(239,68,68,0.08)", color:"#EF4444", fontSize:11, cursor:"pointer" }}>
            🗑 Clear
          </button>
        )}
      </div>
      {loading && <div style={{ fontSize:12, color:C.navyLite }}>Loading...</div>}
      {!loading && data.chars === 0 && (
        <div style={{ fontSize:12, color:C.navyLite, textAlign:"center", padding:"20px 0" }}>
          No knowledge loaded. Upload a file above to start.
        </div>
      )}
      {!loading && data.chars > 0 && (
        <textarea readOnly value={data.texto}
          style={{ width:"100%", height:200, background:C.bg, border:`1px solid rgba(26,43,74,0.08)`, borderRadius:8, padding:"10px 12px", color:C.navyMid, fontSize:11, resize:"vertical", outline:"none", fontFamily:"monospace", boxSizing:"border-box" }}
        />
      )}
    </div>
  );
}

// ── TAB ACTIVIDAD ────────────────────────────────────────────────
function TabActividad({ leads }) {
  const [logs, setLogs]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [filtroTipo, setFiltroTipo] = useState("all");

  const cargar = () => {
    setLoading(true);
    fetch("https://fl.velqo.io/api/actividad?limit=100")
      .then(r => r.json())
      .then(data => { setLogs(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { cargar(); }, []);

  const logsFiltrados = filtroTipo === "all" ? logs : logs.filter(l => l.tipo === filtroTipo);
  const tipoLabel  = { presentacion:"Introduction", seguimiento:"Follow-up", propuesta:"Proposal" };
  const tipoColor  = { presentacion:"#3B82F6", seguimiento:C.gold, propuesta:"#10B981" };

  return (
    <div>
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
        <div style={{ fontSize:15, fontWeight:800, color:C.navy, flex:1 }}>⚡ System activity</div>
        <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
          style={{ background:"#fff", border:`1px solid rgba(26,43,74,0.12)`, borderRadius:8, padding:"7px 12px", color:C.navy, fontSize:12, outline:"none" }}>
          <option value="all">All types</option>
          <option value="presentacion">Introduction</option>
          <option value="seguimiento">Follow-up</option>
          <option value="propuesta">Proposal</option>
        </select>
        <button onClick={cargar} style={{ padding:"7px 14px", borderRadius:8, border:`1px solid rgba(26,43,74,0.1)`, background:"transparent", color:C.navyLite, cursor:"pointer", fontSize:12 }}>↻ Refresh</button>
      </div>

      {loading && <div style={{ fontSize:13, color:C.navyLite, textAlign:"center", padding:40 }}>Loading...</div>}
      {!loading && logsFiltrados.length === 0 && (
        <div style={{ fontSize:13, color:C.navyLite, textAlign:"center", padding:60 }}>No activity recorded yet</div>
      )}
      {!loading && logsFiltrados.length > 0 && (
        <div style={{ background:"#fff", border:`1px solid rgba(26,43,74,0.09)`, borderRadius:14, overflow:"hidden" }}>
          {logsFiltrados.map((log, i) => {
            const lead = leads.find(l => String(l.id) === String(log.lead_id));
            const fecha = new Date(log.creado_en).toLocaleString("en-US", { day:"2-digit", month:"2-digit", year:"2-digit", hour:"2-digit", minute:"2-digit" });
            return (
              <div key={log.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"11px 16px", borderBottom: i < logsFiltrados.length-1 ? `1px solid rgba(26,43,74,0.05)` : "none" }}>
                <span style={{ fontSize:14, minWidth:16 }}>{log.estado === "enviado" ? "✅" : "❌"}</span>
                <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:10, background:`${tipoColor[log.tipo]||"#6B7280"}20`, color:tipoColor[log.tipo]||"#6B7280", whiteSpace:"nowrap" }}>
                  {tipoLabel[log.tipo] || log.tipo}
                </span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, color:C.navy, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lead?.empresa || `Lead #${log.lead_id}`}</div>
                  <div style={{ fontSize:10, color:C.navyLite, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{log.destinatario}</div>
                </div>
                <span style={{ fontSize:10, color:C.navyLite, whiteSpace:"nowrap" }}>{fecha}</span>
              </div>
            );
          })}
        </div>
      )}

      {!loading && logs.length > 0 && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:10, marginTop:16 }}>
          {[
            { label:"Total sent",      value: logs.filter(l=>l.estado==="enviado").length, color:"#10B981" },
            { label:"Introductions",   value: logs.filter(l=>l.tipo==="presentacion").length, color:"#3B82F6" },
            { label:"Follow-ups",      value: logs.filter(l=>l.tipo==="seguimiento").length, color:C.gold    },
            { label:"Errors",          value: logs.filter(l=>l.estado==="error").length, color:"#EF4444"    },
          ].map(k => (
            <div key={k.label} style={{ background:"#fff", border:`1px solid rgba(26,43,74,0.09)`, borderRadius:10, padding:"12px 14px" }}>
              <div style={{ fontSize:22, fontWeight:900, color:k.color }}>{k.value}</div>
              <div style={{ fontSize:10, color:C.navyLite, marginTop:4 }}>{k.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── TEMPLATES ────────────────────────────────────────────────────
const TEMPLATES_DEFAULT = [
  {
    id: "presentacion",
    nombre: "Introduction email",
    tipo: "presentacion",
    html: `<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;font-family:Georgia,'Times New Roman',serif;background:#ffffff;border:1px solid #dde3ed;">
  <tr><td style="padding:32px 40px 24px 40px;background:#1A2B4A;text-align:center;">
    <div style="font-size:22px;font-weight:normal;color:#C8972A;letter-spacing:0.12em;">FERNÁNDEZ LACORT</div>
    <div style="font-size:9px;letter-spacing:0.22em;text-transform:uppercase;color:rgba(255,255,255,0.5);margin-top:6px;">Intellectual Property · Latin America</div>
  </td></tr>
  <tr><td style="padding:36px 40px 24px 40px;">
    <p style="font-size:15px;color:#1A2B4A;line-height:1.7;margin:0 0 16px 0;">Dear <strong>{{contacto}}</strong>,</p>
    <p style="font-size:14px;color:#2D4470;line-height:1.8;margin:0 0 16px 0;">My name is [NAME] from <strong>Fernández Lacort</strong>, a law firm specialized in Intellectual Property across Latin America.</p>
    <p style="font-size:14px;color:#2D4470;line-height:1.8;margin:0 0 24px 0;">We help companies protect their trademarks, patents, and IP assets in Argentina and the entire region — with a fast, efficient process and a dedicated bilingual team.</p>
    <table cellpadding="0" cellspacing="0" style="margin:0 0 28px 0;">
      <tr><td style="background:#C8972A;padding:14px 32px;">
        <a href="mailto:info@fernandezlacort.com.ar" style="color:#ffffff;text-decoration:none;font-family:Georgia,serif;font-size:13px;letter-spacing:0.04em;">Schedule a call →</a>
      </td></tr>
    </table>
    <p style="font-size:13px;color:#6B7A94;line-height:1.7;margin:0;">We look forward to hearing from you.</p>
    <p style="font-size:13px;color:#1A2B4A;margin:16px 0 0 0;">[NAME]<br>Fernández Lacort<br>fernandezlacort.com.ar</p>
  </td></tr>
  <tr><td style="padding:16px 40px;border-top:1px solid #dde3ed;text-align:center;">
    <p style="font-size:10px;color:#aaa;margin:0;letter-spacing:0.1em;">fernandezlacort.com.ar · info@fernandezlacort.com.ar</p>
  </td></tr>
</table>`,
  },
  {
    id: "seguimiento",
    nombre: "Follow-up email",
    tipo: "seguimiento",
    html: `<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;font-family:Georgia,'Times New Roman',serif;background:#ffffff;border:1px solid #dde3ed;">
  <tr><td style="padding:32px 40px 24px 40px;background:#1A2B4A;text-align:center;">
    <div style="font-size:22px;font-weight:normal;color:#C8972A;letter-spacing:0.12em;">FERNÁNDEZ LACORT</div>
    <div style="font-size:9px;letter-spacing:0.22em;text-transform:uppercase;color:rgba(255,255,255,0.5);margin-top:6px;">Intellectual Property · Latin America</div>
  </td></tr>
  <tr><td style="padding:36px 40px 24px 40px;">
    <p style="font-size:15px;color:#1A2B4A;line-height:1.7;margin:0 0 16px 0;">Dear <strong>{{contacto}}</strong>,</p>
    <p style="font-size:14px;color:#2D4470;line-height:1.8;margin:0 0 16px 0;">I wanted to follow up on my previous message regarding trademark protection in Latin America.</p>
    <p style="font-size:14px;color:#2D4470;line-height:1.8;margin:0 0 24px 0;">If you are expanding to the region or already operating here, securing your IP rights is a critical step we can handle efficiently on your behalf.</p>
    <table cellpadding="0" cellspacing="0" style="margin:0 0 28px 0;">
      <tr><td style="background:#C8972A;padding:14px 32px;">
        <a href="mailto:info@fernandezlacort.com.ar" style="color:#ffffff;text-decoration:none;font-family:Georgia,serif;font-size:13px;letter-spacing:0.04em;">Reply to this email →</a>
      </td></tr>
    </table>
    <p style="font-size:13px;color:#1A2B4A;margin:16px 0 0 0;">[NAME]<br>Fernández Lacort</p>
  </td></tr>
  <tr><td style="padding:16px 40px;border-top:1px solid #dde3ed;text-align:center;">
    <p style="font-size:10px;color:#aaa;margin:0;letter-spacing:0.1em;">fernandezlacort.com.ar · info@fernandezlacort.com.ar</p>
  </td></tr>
</table>`,
  },
  {
    id: "propuesta",
    nombre: "Proposal email",
    tipo: "propuesta",
    html: `<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;font-family:Georgia,'Times New Roman',serif;background:#ffffff;border:1px solid #dde3ed;">
  <tr><td style="padding:32px 40px 24px 40px;background:#1A2B4A;text-align:center;">
    <div style="font-size:22px;font-weight:normal;color:#C8972A;letter-spacing:0.12em;">FERNÁNDEZ LACORT</div>
    <div style="font-size:9px;letter-spacing:0.22em;text-transform:uppercase;color:rgba(255,255,255,0.5);margin-top:6px;">Intellectual Property · Latin America</div>
  </td></tr>
  <tr><td style="padding:36px 40px 24px 40px;">
    <p style="font-size:15px;color:#1A2B4A;line-height:1.7;margin:0 0 16px 0;">Dear <strong>{{contacto}}</strong>,</p>
    <p style="font-size:14px;color:#2D4470;line-height:1.8;margin:0 0 16px 0;">Thank you for your interest in our services. Please find below a summary of our proposal for <strong>{{empresa}}</strong>.</p>
    <table cellpadding="0" cellspacing="0" width="100%" style="margin:0 0 20px 0;border:1px solid #dde3ed;">
      <tr><td style="padding:14px 18px;background:#F5F4F0;">
        <p style="font-size:12px;text-transform:uppercase;letter-spacing:0.1em;color:#6B7A94;margin:0 0 8px 0;">Services included</p>
        <p style="font-size:13px;color:#1A2B4A;margin:0;line-height:1.7;">· Trademark registration in selected countries<br>· Full prosecution and follow-up<br>· Bilingual communication throughout the process</p>
      </td></tr>
    </table>
    <table cellpadding="0" cellspacing="0" style="margin:0 0 28px 0;">
      <tr><td style="background:#C8972A;padding:14px 32px;">
        <a href="mailto:info@fernandezlacort.com.ar" style="color:#ffffff;text-decoration:none;font-family:Georgia,serif;font-size:13px;letter-spacing:0.04em;">View full proposal →</a>
      </td></tr>
    </table>
    <p style="font-size:13px;color:#1A2B4A;margin:16px 0 0 0;">[NAME]<br>Fernández Lacort</p>
  </td></tr>
  <tr><td style="padding:16px 40px;border-top:1px solid #dde3ed;text-align:center;">
    <p style="font-size:10px;color:#aaa;margin:0;letter-spacing:0.1em;">fernandezlacort.com.ar · info@fernandezlacort.com.ar</p>
  </td></tr>
</table>`,
  },
];

const TIPOS_EMAIL = [
  { id: "presentacion", label: "Introduction" },
  { id: "seguimiento",  label: "Follow-up"    },
  { id: "propuesta",    label: "Proposal"      },
  { id: "custom",       label: "Custom"        },
];

// ── TAB EMAIL EDITOR ─────────────────────────────────────────────
function TabEmailEditor() {
  const [templates, setTemplates]       = useState(null);
  const [seleccionado, setSeleccionado] = useState(null);
  const [editando, setEditando]         = useState(null);
  const [vista, setVista]               = useState("editor");
  const [guardado, setGuardado]         = useState(false);
  const [guardando, setGuardando]       = useState(false);
  const [generando, setGenerando]       = useState(false);
  const [brief, setBrief]               = useState("");
  const [nuevoNombre, setNuevoNombre]   = useState("");
  const [nuevoTipo, setNuevoTipo]       = useState("presentacion");
  const [creando, setCreando]           = useState(false);
  const [errorCarga, setErrorCarga]     = useState(false);

  useEffect(() => {
    fetch("https://fl.velqo.io/api/templates/fl")
      .then(r => r.json())
      .then(data => {
        // Server returns object {presentacion, seguimiento, propuesta} — convert to array
        let arr;
        if (Array.isArray(data) && data.length > 0) {
          arr = data;
        } else if (data && typeof data === "object" && !Array.isArray(data)) {
          arr = TEMPLATES_DEFAULT; // use defaults, server has old format
        } else {
          arr = TEMPLATES_DEFAULT;
        }
        setTemplates(arr);
        setSeleccionado(arr[0]?.id || null);
      })
      .catch(() => { setTemplates(TEMPLATES_DEFAULT); setSeleccionado(TEMPLATES_DEFAULT[0]?.id || null); setErrorCarga(true); });
  }, []);

  const persistir = async (arr) => {
    setGuardando(true);
    try {
      await fetch("https://fl.velqo.io/api/templates/fl", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(arr) });
    } catch(e) { console.warn("Could not save to server"); }
    setGuardando(false);
  };

  const tpl = editando || (templates || []).find(t => t.id === seleccionado);
  const seleccionar = (id) => { setSeleccionado(id); setEditando(null); setVista("editor"); setGuardado(false); };
  const cambiarHtml = (html) => { setEditando({ ...tpl, html }); setGuardado(false); };
  const cambiarNombre = (nombre) => { setEditando({ ...tpl, nombre }); setGuardado(false); };
  const cambiarTipo = (tipo) => { setEditando({ ...tpl, tipo }); setGuardado(false); };

  const guardar = async () => {
    const updated = templates.map(t => t.id === tpl.id ? tpl : t);
    await persistir(updated); setTemplates(updated); setEditando(null); setGuardado(true);
    setTimeout(() => setGuardado(false), 2500);
  };

  const crearTemplate = async () => {
    if (!nuevoNombre.trim()) return;
    const id = "tpl_" + Date.now();
    const nuevo = { id, nombre: nuevoNombre.trim(), tipo: nuevoTipo,
      html: `<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;font-family:Georgia,serif;background:#ffffff;border:1px solid #dde3ed;">
  <tr><td style="padding:32px 40px;background:#1A2B4A;text-align:center;">
    <div style="font-size:22px;color:#C8972A;letter-spacing:0.12em;">FERNÁNDEZ LACORT</div>
  </td></tr>
  <tr><td style="padding:36px 40px;">
    <p style="font-size:14px;color:#2D4470;line-height:1.8;margin:0;">Write your email content here...</p>
  </td></tr>
  <tr><td style="padding:16px 40px;border-top:1px solid #dde3ed;text-align:center;">
    <p style="font-size:10px;color:#aaa;margin:0;">fernandezlacort.com.ar · info@fernandezlacort.com.ar</p>
  </td></tr>
</table>` };
    const updated = [...templates, nuevo];
    await persistir(updated); setTemplates(updated); setSeleccionado(id); setEditando(null); setCreando(false); setNuevoNombre(""); setNuevoTipo("presentacion");
  };

  const eliminarTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    const updated = templates.filter(t => t.id !== id);
    await persistir(updated); setTemplates(updated); setSeleccionado(updated[0]?.id || null); setEditando(null);
  };

  const generarConIA = async () => {
    if (!brief.trim()) return;
    setGenerando(true);
    try {
      const resp = await fetch("https://api.anthropic.com/v1/messages", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          model: "claude-sonnet-4-6", max_tokens: 1000,
          messages: [{ role:"user", content:`You are an expert in B2B email marketing for Fernández Lacort, an Argentine law firm specializing in Intellectual Property registration across Latin America.

Generate ONLY the HTML of a sales email in English based on this brief:
"${brief}"

STRICT RULES:
- Return ONLY the HTML table, no <!DOCTYPE>, no <html>, no explanation
- Use HTML tables with inline styles (Gmail-compatible)
- Color palette: header background #1A2B4A (navy), header text #C8972A (gold), body background #ffffff, body text #1A2B4A and #2D4470, footer #F5F4F0
- Typography: Georgia serif
- Structure: navy/gold header with FERNÁNDEZ LACORT → email body → CTA button (gold background #C8972A) → signature → footer
- Tone: professional, international, concise, no emojis
- Allowed variables: {{contacto}}, {{empresa}}
- HTML must start with <table and end with </table>` }],
        }),
      });
      const data = await resp.json();
      const html = data.content?.[0]?.text || "";
      cambiarHtml(html.replace(/\`\`\`html?/gi,"").replace(/\`\`\`/g,"").trim());
      setBrief("");
    } catch(e) { alert("Error connecting to AI"); }
    setGenerando(false);
  };

  const tipoColor = { presentacion:"#3B82F6", seguimiento:C.gold, propuesta:"#10B981", custom:"#8B5CF6" };

  if (templates === null) return <div style={{ padding:60, textAlign:"center", color:C.navyLite, fontSize:13 }}>Loading templates...</div>;

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontWeight:800, fontSize:18, color:C.navy, marginBottom:3 }}>✉️ Email editor</div>
          <div style={{ fontSize:12, color:C.navyLite }}>Create and edit email templates. Hermes uses them automatically based on type.</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          {guardando && <span style={{ fontSize:11, color:C.navyLite, fontStyle:"italic" }}>Saving...</span>}
          {guardado && <span style={{ fontSize:11, color:"#10B981" }}>✓ Saved</span>}
          <button onClick={() => setCreando(true)} style={{ padding:"9px 18px", borderRadius:8, border:`1px solid ${C.navy}`, background:C.navy, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>
            + New template
          </button>
        </div>
      </div>

      {creando && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16 }}>
          <div style={{ background:C.bg, border:`1px solid ${C.rule}`, borderRadius:16, padding:28, width:"100%", maxWidth:400 }}>
            <div style={{ fontSize:16, fontWeight:700, color:C.navy, marginBottom:20 }}>New email template</div>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:11, color:C.navyLite, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:6 }}>Name</label>
              <input value={nuevoNombre} onChange={e => setNuevoNombre(e.target.value)} placeholder="e.g. China introduction email"
                style={{ width:"100%", background:"#fff", border:`1px solid ${C.rule}`, borderRadius:8, padding:"10px 14px", color:C.navy, fontSize:13, outline:"none", boxSizing:"border-box" }}/>
            </div>
            <div style={{ marginBottom:22 }}>
              <label style={{ fontSize:11, color:C.navyLite, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:6 }}>Type</label>
              <select value={nuevoTipo} onChange={e => setNuevoTipo(e.target.value)}
                style={{ width:"100%", background:"#fff", border:`1px solid ${C.rule}`, borderRadius:8, padding:"10px 14px", color:C.navy, fontSize:13, outline:"none", boxSizing:"border-box" }}>
                {TIPOS_EMAIL.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={() => setCreando(false)} style={{ flex:1, padding:"10px", borderRadius:8, border:`1px solid ${C.rule}`, background:"transparent", color:C.navyLite, cursor:"pointer", fontSize:13 }}>Cancel</button>
              <button onClick={crearTemplate} disabled={!nuevoNombre.trim()}
                style={{ flex:2, padding:"10px", borderRadius:8, border:"none", background: nuevoNombre.trim() ? C.navy : "rgba(26,43,74,0.1)", color: nuevoNombre.trim() ? "#fff" : C.navyLite, fontWeight:700, fontSize:13, cursor: nuevoNombre.trim() ? "pointer" : "not-allowed" }}>
                Create template
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"260px 1fr", gap:16, alignItems:"start" }}>
        <div style={{ background:"#fff", border:`1px solid rgba(26,43,74,0.09)`, borderRadius:14, overflow:"hidden" }}>
          <div style={{ padding:"12px 16px", borderBottom:`1px solid rgba(26,43,74,0.07)` }}>
            <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:"0.1em", color:C.navyLite }}>Templates ({templates.length})</div>
          </div>
          {templates.map(t => (
            <div key={t.id} onClick={() => seleccionar(t.id)}
              style={{ padding:"13px 16px", background: seleccionado===t.id ? "rgba(26,43,74,0.05)" : "transparent", borderLeft: seleccionado===t.id ? `3px solid ${C.gold}` : "3px solid transparent", cursor:"pointer", borderBottom:`1px solid rgba(26,43,74,0.05)`, transition:"background 0.1s" }}>
              <div style={{ fontSize:13, fontWeight: seleccionado===t.id ? 700 : 500, color:C.navy, marginBottom:4 }}>{t.nombre}</div>
              <span style={{ fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:10, background:`${tipoColor[t.tipo]||"#6B7280"}18`, color:tipoColor[t.tipo]||"#6B7280" }}>
                {TIPOS_EMAIL.find(x=>x.id===t.tipo)?.label || t.tipo}
              </span>
            </div>
          ))}
        </div>

        {tpl ? (
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
              <input value={tpl.nombre} onChange={e => cambiarNombre(e.target.value)}
                style={{ flex:1, minWidth:180, background:"#fff", border:`1px solid rgba(26,43,74,0.12)`, borderRadius:8, padding:"9px 14px", color:C.navy, fontSize:14, fontWeight:600, outline:"none" }}/>
              <select value={tpl.tipo} onChange={e => cambiarTipo(e.target.value)}
                style={{ background:"#fff", border:`1px solid rgba(26,43,74,0.12)`, borderRadius:8, padding:"9px 12px", color:C.navy, fontSize:13, outline:"none" }}>
                {TIPOS_EMAIL.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <div style={{ display:"flex", gap:8 }}>
                {["editor","preview"].map(v => (
                  <button key={v} onClick={() => setVista(v)} style={{ padding:"8px 14px", borderRadius:8, border:`1px solid ${vista===v ? C.gold : "rgba(26,43,74,0.12)"}`, background: vista===v ? `${C.gold}15` : "transparent", color: vista===v ? C.gold : C.navyLite, fontSize:12, cursor:"pointer", fontWeight: vista===v ? 700 : 400 }}>
                    {v === "editor" ? "Editor" : "Preview"}
                  </button>
                ))}
              </div>
              {editando && (
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={guardar} style={{ padding:"8px 18px", borderRadius:8, border:"none", background:C.navy, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>Save</button>
                  <button onClick={() => setEditando(null)} style={{ padding:"8px 14px", borderRadius:8, border:`1px solid rgba(26,43,74,0.12)`, background:"transparent", color:C.navyLite, fontSize:12, cursor:"pointer" }}>Discard</button>
                </div>
              )}
              <button onClick={() => eliminarTemplate(tpl.id)} style={{ padding:"8px 12px", borderRadius:8, border:"1px solid rgba(239,68,68,0.3)", background:"rgba(239,68,68,0.08)", color:"#EF4444", fontSize:11, cursor:"pointer" }}>🗑</button>
            </div>

            {vista === "editor" && (
              <div style={{ background:"#fff", border:`1px solid rgba(26,43,74,0.09)`, borderRadius:12, overflow:"hidden" }}>
                <div style={{ padding:"10px 16px", borderBottom:`1px solid rgba(26,43,74,0.07)`, display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:11, color:C.navyLite, textTransform:"uppercase", letterSpacing:"0.1em" }}>Email HTML</span>
                  <span style={{ fontSize:10, color:"rgba(26,43,74,0.25)" }}>· Gmail-compatible · inline styles · tables</span>
                </div>
                <textarea value={tpl.html} onChange={e => cambiarHtml(e.target.value)} spellCheck={false}
                  style={{ width:"100%", height:360, background:"#fff", border:"none", padding:"16px 18px", color:C.navy, fontSize:12, fontFamily:"'Courier New',monospace", resize:"vertical", outline:"none", boxSizing:"border-box", lineHeight:1.6 }}/>
              </div>
            )}

            {vista === "preview" && (
              <div style={{ background:C.bgWarm, borderRadius:12, padding:24, overflow:"hidden" }}>
                <div style={{ fontSize:11, color:C.navyLite, marginBottom:12, textTransform:"uppercase", letterSpacing:"0.1em" }}>Preview — similar to Gmail</div>
                <iframe srcDoc={`<!DOCTYPE html><html><body style="margin:0;padding:0;background:${C.bgWarm};">${tpl.html}</body></html>`}
                  style={{ width:"100%", border:"none", borderRadius:8, minHeight:420, background:"transparent" }} title="preview" sandbox="allow-same-origin"/>
              </div>
            )}

            <div style={{ background:"#fff", border:`1px solid rgba(26,43,74,0.09)`, borderRadius:12, padding:18 }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.navy, marginBottom:4 }}>✨ AI Generator</div>
              <div style={{ fontSize:11, color:C.navyLite, marginBottom:12 }}>Describe the email you need and AI will generate it following FL brand identity.</div>
              <div style={{ display:"flex", gap:10 }}>
                <textarea value={brief} onChange={e => setBrief(e.target.value)}
                  placeholder='e.g. "Introduction email for Chinese manufacturing companies expanding to LATAM, emphasizing our experience with Asian clients and fast turnaround."'
                  style={{ flex:1, background:"#fff", border:`1px solid rgba(26,43,74,0.12)`, borderRadius:8, padding:"10px 14px", color:C.navy, fontSize:12, resize:"none", outline:"none", minHeight:72, fontFamily:"Georgia,serif", lineHeight:1.6 }}/>
                <button onClick={generarConIA} disabled={generando || !brief.trim()}
                  style={{ padding:"10px 20px", borderRadius:8, border:"none", background: generando || !brief.trim() ? "rgba(26,43,74,0.1)" : C.navy, color: generando || !brief.trim() ? C.navyLite : "#fff", fontWeight:700, fontSize:12, cursor: generando || !brief.trim() ? "not-allowed" : "pointer", whiteSpace:"nowrap", alignSelf:"stretch" }}>
                  {generando ? "⏳ Generating..." : "✨ Generate\nwith AI"}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ padding:40, textAlign:"center", color:C.navyLite, fontSize:13 }}>Select a template to edit it</div>
        )}
      </div>
    </div>
  );
}

// ── APP PRINCIPAL ────────────────────────────────────────────────
export default function App() {
  const isMobile = useIsMobile();
  const [usuario, setUsuario]                 = useLocalStorage("fl_usuario", null);
  const [leads, setLeads]                     = useLocalStorage("fl_leads", LEADS_DEMO);
  const [mercados, setMercados]               = useLocalStorage("fl_mercados", MERCADOS_INICIALES);
  const [tab, setTab]                         = useState("dashboard");
  const [filtroMercado, setFiltroMercado]     = useState("All");
  const [filtroSegmento, setFiltroSegmento]   = useState("All");
  const [busqueda, setBusqueda]               = useState("");
  const [dragId, setDragId]                   = useState(null);
  const [modal, setModal]                     = useState(null);
  const [configs, setConfigs]                 = useState({});
  const [pendientes, setPendientes]           = useState({});

  const vendedores = VENDEDORES;
  const esAdmin = usuario?.rol === "admin";

  useLeadsFromFile(setLeads);

  useEffect(() => {
    if (!esAdmin) return;
    ["fl"].forEach(emp => {
      fetch(`https://fl.velqo.io/api/config/${emp}`).then(r => r.json()).then(data => setConfigs(prev => ({...prev,[emp]:data}))).catch(() => {});
      fetch(`https://fl.velqo.io/api/pendientes/${emp}`).then(r => r.json()).then(data => setPendientes(prev => ({...prev,[emp]:data.pendientes||0}))).catch(() => {});
    });
  }, [esAdmin]);

  const leadsFiltrados = leads.filter(l => {
    if (filtroMercado !== "All" && l.mercado !== filtroMercado && l.region !== filtroMercado) return false;
    if (filtroSegmento !== "All" && l.segmento !== filtroSegmento) return false;
    if (busqueda && !`${l.empresa}${l.contacto}${l.ciudad}${l.mercado}`.toLowerCase().includes(busqueda.toLowerCase())) return false;
    return true;
  });

  const guardarLeads = async (nuevos) => {
    setLeads(nuevos);
    try {
      await fetch("https://fl.velqo.io/api/leads", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(nuevos) });
    } catch(e) {}
  };

  const confirmarMover = ({ monto, motivo }) => {
    if (!modal) return;
    const updated = leads.map(l => l.id === modal.lead.id ? { ...l, estado:modal.destino, monto: monto||l.monto, notas: motivo ? `${l.notas ? l.notas+"\n" : ""}Lost: ${motivo}` : l.notas } : l);
    guardarLeads(updated);
    setModal(null);
  };

  const asignarVendedor = (leadId, vendedorId) => {
    const updated = leads.map(l => l.id === leadId ? {...l, vendedor:vendedorId} : l);
    guardarLeads(updated);
  };

  const onDragStart = (e, id) => { setDragId(id); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver  = e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop      = (e, colId) => {
    e.preventDefault();
    if (!dragId) return;
    const lead = leads.find(l => l.id === dragId);
    if (!lead || lead.estado === colId) { setDragId(null); return; }
    if (colId === "Lost" || colId === "Quoted" || colId === "Won") {
      setModal({ tipo:"mover", lead, destino:colId });
    } else {
      const updated = leads.map(l => l.id === dragId ? {...l, estado:colId} : l);
      guardarLeads(updated);
    }
    setDragId(null);
  };

  const segmentos = ["All", ...Array.from(new Set(leads.map(l=>l.segmento).filter(Boolean)))];
  const totalLeads = leads.length;
  const totalWon = leads.filter(l=>l.estado==="Won").reduce((a,b)=>a+b.monto,0);
  const enCurso = leads.filter(l=>!["Won","Lost"].includes(l.estado)).length;

  const TABS = [
    { id:"dashboard", label:"Dashboard" },
    { id:"emails",    label:"✉️ Emails" },
    { id:"actividad", label:"⚡ Activity" },
    { id:"config",    label:"⚙️ Config" },
  ];

  if (!usuario) return <Login vendedores={vendedores} onLogin={setUsuario}/>;

  // ── NAV ──────────────────────────────────────────────────────────
  const nav = (
    <div style={{ background:C.navy, padding: isMobile ? "12px 16px" : "0 32px", display:"flex", alignItems:"center", gap: isMobile ? 12 : 32, flexWrap:"wrap", justifyContent:"space-between", minHeight: isMobile ? "auto" : 56, position:"sticky", top:0, zIndex:100 }}>
      <div style={{ display:"flex", alignItems:"center", gap:16 }}>
        <div style={{ fontFamily:"Georgia,serif", fontSize:15, color:C.gold, letterSpacing:"0.08em", fontWeight:"normal" }}>
          Fernández Lacort
        </div>
        <div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", letterSpacing:"0.15em", textTransform:"uppercase" }}>IP · LATAM</div>
      </div>
      <div style={{ display:"flex", gap: isMobile ? 8 : 4 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: isMobile ? "6px 10px" : "18px 16px", background:"transparent", border:"none", borderBottom: tab===t.id ? `2px solid ${C.gold}` : "2px solid transparent", color: tab===t.id ? C.gold : "rgba(255,255,255,0.55)", fontSize: isMobile ? 11 : 12, fontWeight: tab===t.id ? 700 : 400, cursor:"pointer", transition:"color 0.15s", whiteSpace:"nowrap" }}>
            {t.label}
          </button>
        ))}
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <span style={{ fontSize:11, color:"rgba(255,255,255,0.4)" }}>{usuario.nombre}</span>
        <button onClick={() => setUsuario(null)} style={{ padding:"5px 12px", borderRadius:6, border:"1px solid rgba(255,255,255,0.15)", background:"transparent", color:"rgba(255,255,255,0.4)", fontSize:11, cursor:"pointer" }}>Exit</button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", background:C.bg, fontFamily:"Georgia,'Times New Roman',serif" }}>
      {nav}
      <div style={{ padding: isMobile ? "16px" : "24px 32px", maxWidth:1600, margin:"0 auto" }}>

        {/* ── DASHBOARD ── */}
        {tab === "dashboard" && (
          <div>
            {/* KPIs */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12, marginBottom:28 }}>
              {[
                { label:"Total leads",    value:totalLeads,         color:C.navy  },
                { label:"In progress",    value:enCurso,            color:C.navyMid },
                { label:"Won (USD)",      value:fmt$(totalWon),     color:C.gold  },
                { label:"Pending queue",  value:pendientes["fl"]||0,color:"#6B7280" },
              ].map(k => (
                <div key={k.label} style={{ background:"#fff", border:`1px solid rgba(26,43,74,0.09)`, borderRadius:12, padding:"16px 20px" }}>
                  <div style={{ fontSize:24, fontWeight:800, color:k.color }}>{k.value}</div>
                  <div style={{ fontSize:11, color:C.navyLite, marginTop:4 }}>{k.label}</div>
                </div>
              ))}
            </div>

            {/* Filtros */}
            <div style={{ display:"flex", gap:10, marginBottom:20, flexWrap:"wrap", alignItems:"center" }}>
              <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Search company, contact..."
                style={{ flex:1, minWidth:180, background:"#fff", border:`1px solid rgba(26,43,74,0.12)`, borderRadius:8, padding:"9px 14px", color:C.navy, fontSize:13, outline:"none" }}/>
              <select value={filtroMercado} onChange={e => setFiltroMercado(e.target.value)}
                style={{ background:"#fff", border:`1px solid rgba(26,43,74,0.12)`, borderRadius:8, padding:"9px 12px", color:C.navy, fontSize:13, outline:"none" }}>
                <option value="All">All markets</option>
                {mercados.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select value={filtroSegmento} onChange={e => setFiltroSegmento(e.target.value)}
                style={{ background:"#fff", border:`1px solid rgba(26,43,74,0.12)`, borderRadius:8, padding:"9px 12px", color:C.navy, fontSize:13, outline:"none" }}>
                {segmentos.map(s => <option key={s} value={s}>{s === "All" ? "All segments" : s}</option>)}
              </select>
              <span style={{ fontSize:12, color:C.navyLite }}>{leadsFiltrados.length} leads</span>
            </div>

            {/* Kanban */}
            <div style={{ display:"flex", gap:12, overflowX:"auto", paddingBottom:20, marginBottom:8 }}>
              {COLUMNAS.map(col => (
                <Columna key={col.id} col={col}
                  leads={leadsFiltrados.filter(l=>l.estado===col.id)}
                  onDrop={onDrop} onDragOver={onDragOver} onDragStart={onDragStart}
                  onTap={lead => setModal({ tipo:"detalle", lead })}/>
              ))}
            </div>

            {/* Analytics */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(320px,1fr))", gap:20, marginTop:28 }}>
              {/* Leads por mercado */}
              {(() => {
                const mCols = { LATAM:"#10B981", China:"#EF4444", France:"#3B82F6", Italy:"#22C55E", Germany:"#6B7280" };
                const data = mercados.map(m => ({
                  label:m, color:mCols[m]||C.gold,
                  total: leads.filter(l=>l.mercado===m||l.region===m).length,
                })).filter(d=>d.total>0);
                const totalL = data.reduce((a,b)=>a+b.total,0)||1;
                return (
                  <div style={{ background:"#fff", border:`1px solid rgba(26,43,74,0.09)`, borderRadius:14, padding:20 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:C.navy, marginBottom:16 }}>Leads by market</div>
                    {data.map(d => (
                      <div key={d.label} style={{ marginBottom:10 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                          <span style={{ fontSize:12, color:C.navyMid }}>{d.label}</span>
                          <div style={{ display:"flex", gap:8 }}>
                            <span style={{ fontSize:12, fontWeight:700, color:d.color }}>{d.total}</span>
                            <span style={{ fontSize:10, color:C.navyLite }}>{Math.round(d.total/totalL*100)}%</span>
                          </div>
                        </div>
                        <div style={{ height:6, background:"rgba(26,43,74,0.06)", borderRadius:3 }}>
                          <div style={{ height:"100%", width:`${Math.round(d.total/totalL*100)}%`, background:d.color, borderRadius:3 }}/>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Leads por segmento */}
              {(() => {
                const segs = Array.from(new Set(leads.map(l=>l.segmento).filter(Boolean)));
                const segCols = [C.navy, C.gold, "#10B981","#3B82F6","#8B5CF6","#EF4444","#06B6D4","#EC4899"];
                const data = segs.map((s,i) => ({
                  label:s, color:segCols[i%8],
                  total: leads.filter(l=>l.segmento===s).length,
                  ganados: leads.filter(l=>l.segmento===s&&l.estado==="Won").length,
                  monto: leads.filter(l=>l.segmento===s&&l.monto>0).reduce((a,b)=>a+b.monto,0),
                })).sort((a,b)=>b.total-a.total);
                const max = Math.max(...data.map(d=>d.total),1);
                return (
                  <div style={{ background:"#fff", border:`1px solid rgba(26,43,74,0.09)`, borderRadius:14, padding:20 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:C.navy, marginBottom:16 }}>Leads by segment</div>
                    {data.map(d => (
                      <div key={d.label} style={{ marginBottom:12 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                          <span style={{ fontSize:12, color:C.navyMid }}>{d.label}</span>
                          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                            {d.ganados>0 && <span style={{ fontSize:10, color:"#10B981" }}>✓ {d.ganados} won</span>}
                            {d.monto>0 && <span style={{ fontSize:10, color:C.gold }}>{fmt$(d.monto)}</span>}
                            <span style={{ fontSize:12, fontWeight:700, color:d.color }}>{d.total}</span>
                          </div>
                        </div>
                        <div style={{ height:6, background:"rgba(26,43,74,0.06)", borderRadius:3 }}>
                          <div style={{ height:"100%", width:`${Math.round(d.total/max*100)}%`, background:d.color, borderRadius:3 }}/>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* ── EMAILS ── */}
        {tab === "emails" && <TabEmailEditor/>}

        {/* ── ACTIVIDAD ── */}
        {tab === "actividad" && <TabActividad leads={leads}/>}

        {/* ── CONFIG ── */}
        {tab === "config" && esAdmin && (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(340px,1fr))", gap:24 }}>
            {/* Knowledge */}
            <div>
              <div style={{ fontWeight:700, fontSize:14, color:C.navy, marginBottom:12 }}>📚 Knowledge — FL</div>
              <div style={{ background:"#fff", border:`1px solid rgba(26,43,74,0.09)`, borderRadius:12, padding:16, marginBottom:12 }}>
                <div style={{ fontSize:12, color:C.navyLite, marginBottom:8 }}>Upload PDF, Word, or Excel to update the agent's knowledge base</div>
                <input type="file" accept=".pdf,.doc,.docx,.xlsx,.xls,.txt"
                  onChange={async e => {
                    const file = e.target.files[0]; if (!file) return;
                    const body = await file.arrayBuffer();
                    fetch(`https://fl.velqo.io/api/knowledge/fl`, { method:"POST", headers:{"Content-Type":"application/octet-stream","x-filename":file.name}, body })
                      .then(r => r.json()).then(d => alert(d.ok ? "✅ Knowledge updated" : `❌ Error: ${d.error}`)).catch(() => alert("Connection error"));
                  }}
                  style={{ width:"100%", fontSize:12, color:C.navyLite }}/>
              </div>
              <KnowledgeViewer empresa="fl"/>
            </div>

            {/* Markets & Config */}
            <div>
              <div style={{ fontWeight:700, fontSize:14, color:C.navy, marginBottom:12 }}>🌍 Active markets</div>
              <div style={{ background:"#fff", border:`1px solid rgba(26,43,74,0.09)`, borderRadius:14, padding:20 }}>
                {mercados.map(m => {
                  const mCols = { LATAM:"#10B981", China:"#EF4444", France:"#3B82F6", Italy:"#22C55E", Germany:"#6B7280" };
                  return (
                    <div key={m} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 12px", background:`${mCols[m]||C.gold}10`, border:`1px solid ${mCols[m]||C.gold}30`, borderRadius:8, marginBottom:8 }}>
                      <div style={{ width:6, height:6, borderRadius:"50%", background:mCols[m]||C.gold }}/>
                      <span style={{ fontSize:13, color:C.navyMid, flex:1 }}>{m}</span>
                      <span style={{ fontSize:10, color:C.navyLite }}>{leads.filter(l=>l.mercado===m||l.region===m).length} leads</span>
                    </div>
                  );
                })}
              </div>

              <div style={{ fontWeight:700, fontSize:14, color:C.navy, marginTop:20, marginBottom:12 }}>⚙️ Agent config — FL</div>
              <div style={{ background:"#fff", border:`1px solid rgba(26,43,74,0.09)`, borderRadius:14, padding:20 }}>
                {configs["fl"] ? (
                  Object.entries(configs["fl"]).map(([k,v]) => (
                    <div key={k} style={{ display:"flex", gap:12, padding:"6px 0", borderBottom:`1px solid rgba(26,43,74,0.05)` }}>
                      <span style={{ fontSize:12, color:C.navyLite, minWidth:120, fontFamily:"monospace" }}>{k}</span>
                      <span style={{ fontSize:12, color:C.navy }}>{String(v)}</span>
                    </div>
                  ))
                ) : <div style={{ fontSize:12, color:C.navyLite }}>Loading config...</div>}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* MODALES */}
      {modal?.tipo === "mover" && (
        <ModalMover lead={modal.lead} destino={modal.destino} onConfirm={confirmarMover} onCancel={() => setModal(null)}/>
      )}
      {modal?.tipo === "detalle" && (
        <ModalDetalle lead={modal.lead} vendedores={vendedores} onClose={() => setModal(null)} onAsignar={asignarVendedor} esAdmin={esAdmin} usuarioActual={usuario}/>
      )}
    </div>
  );
}
